/**
 * EducationHome — /education
 *
 * Landing page: hero + browse-by-type strip + featured institutions
 * + open scholarships + feature-value grid.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap, Building2, Award, PlusCircle, ArrowRight,
  BookOpen, Users, Sparkles, Video, MapPin, Calendar,
} from "lucide-react";
import { http } from "@/lib/api";
import EducationCategoryNav from "@/components/EducationCategoryNav";
import TopicNewsStrip from "@/components/TopicNewsStrip";
import { useLocality } from "@/contexts/LocalityContext";

export default function EducationHome() {
  const [home, setHome] = useState({ featured_institutions: [], open_scholarships: [], type_counts: [] });
  const [meta, setMeta] = useState(null);
  const [local, setLocal] = useState({ institutions: [], scholarships: [] });
  const { locality } = useLocality();

  useEffect(() => {
    (async () => {
      const [h, m] = await Promise.all([
        http.get("/education/home").then(r => r.data).catch(() => ({})),
        http.get("/education/meta").then(r => r.data).catch(() => null),
      ]);
      setHome({ featured_institutions: [], open_scholarships: [], type_counts: [], ...h });
      setMeta(m);
    })();
  }, []);

  useEffect(() => {
    if (!locality?.country && !locality?.city) { setLocal({ institutions: [], scholarships: [] }); return; }
    const params = { limit: 6 };
    if (locality?.country) params.country = locality.country;
    if (locality?.city) params.city = locality.city;
    (async () => {
      const [ins, sch] = await Promise.all([
        http.get("/education/institutions", { params }).then(r => r.data?.items || []).catch(() => []),
        http.get("/education/scholarships", { params }).then(r => r.data?.items || []).catch(() => []),
      ]);
      setLocal({ institutions: ins, scholarships: sch });
    })();
  }, [locality?.country, locality?.state, locality?.city]);

  const typeLabel = (slug) => meta?.institution_types?.find(t => t.slug === slug)?.label || slug;

  return (
    <>
      <EducationCategoryNav />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="education-home">

        {/* Latest Education news */}
        <TopicNewsStrip topic="Education" q="education" testId="education-news-strip" />

        <section className="rounded-xl p-6 md:p-10 mb-8" style={{ background: "var(--wofy-surface)" }}>
          <div className="overline mb-2">SOCIAL PULSE EDUCATION</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">
            Learn. Grow. Connect. Inspire.
          </h1>
          <p className="max-w-2xl opacity-80">
            Whether you're looking for the right preschool, applying to a university, searching for
            scholarships, or reconnecting with alumni — Social Pulse Education brings together
            students, parents, educators, institutions, and lifelong learners in one trusted community.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/education/institutions/new" className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="cta-register-institution">
              <PlusCircle className="w-4 h-4" /> Register an institution
            </Link>
            <Link to="/education/scholarships/new" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border hover:bg-black/5" style={{ borderColor: "var(--wofy-border)" }} data-testid="cta-post-scholarship">
              <Award className="w-4 h-4" /> Post a scholarship
            </Link>
            <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full" style={{ background: "#DCFCE7", color: "#166534" }}>
              <Video className="w-3.5 h-3.5" /> Institution &amp; scholarship pages support YouTube / Vimeo embeds
            </span>
          </div>
        </section>

        {/* Near You — location-scoped strip */}
        {(locality?.city || locality?.country) && (
          <section className="mb-10" data-testid="education-near-you">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div>
                <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
                  <MapPin className="w-3 h-3 inline mr-1 -mt-0.5" />
                  EDUCATION NEAR YOU · {[locality.city, locality.state, locality.country].filter(Boolean).join(", ").toUpperCase()}
                </div>
                <h2 className="font-display text-2xl">Local schools & scholarships</h2>
              </div>
              <Link
                to={`/education/institutions?${locality?.country ? `country=${encodeURIComponent(locality.country)}` : ""}${locality?.city ? `&city=${encodeURIComponent(locality.city)}` : ""}`}
                className="text-sm wofy-link inline-flex items-center gap-0.5"
              >
                See all local <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            {local.institutions.length === 0 && local.scholarships.length === 0 ? (
              <div className="wofy-card !p-6 text-center opacity-80">
                <p className="mb-3">No institutions or scholarships listed for your area yet.</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <Link to="/education/institutions/new" className="wofy-btn-primary inline-flex items-center gap-1.5 text-sm">
                    <PlusCircle className="w-4 h-4" /> Register the first institution
                  </Link>
                  <Link to="/education/scholarships/new" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border hover:bg-black/5" style={{ borderColor: "var(--wofy-border)" }}>
                    <Award className="w-4 h-4" /> Post a scholarship
                  </Link>
                </div>
              </div>
            ) : (
              <>
                {local.institutions.length > 0 && (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-4">
                    {local.institutions.slice(0, 3).map(i => <InstitutionCard key={i.id} i={i} typeLabel={typeLabel} />)}
                  </div>
                )}
                {local.scholarships.length > 0 && (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {local.scholarships.slice(0, 3).map(s => <ScholarshipCard key={s.id} s={s} />)}
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* Browse by type */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl">Browse by type</h2>
            <Link to="/education/institutions" className="text-sm wofy-link inline-flex items-center gap-0.5">
              All institutions <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {(meta?.institution_types || []).map(t => {
              const count = home.type_counts.find(x => x.institution_type === t.slug)?.count || 0;
              return (
                <Link key={t.slug} to={`/education/institutions?institution_type=${t.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border hover:bg-black/5 transition"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid={`type-${t.slug}`}>
                  <Building2 className="w-3.5 h-3.5" />{t.label}
                  {count > 0 && <span className="text-[10px] opacity-60">· {count}</span>}
                </Link>
              );
            })}
          </div>
        </section>

        {/* Featured institutions */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl">Featured institutions</h2>
            <Link to="/education/institutions" className="text-sm wofy-link inline-flex items-center gap-0.5">
              Browse all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {home.featured_institutions.length === 0 ? (
            <div className="wofy-card !p-6 text-center opacity-70">
              No institutions yet. <Link to="/education/institutions/new" className="wofy-link">Be the first to register →</Link>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {home.featured_institutions.map(i => <InstitutionCard key={i.id} i={i} typeLabel={typeLabel} />)}
            </div>
          )}
        </section>

        {/* Open scholarships */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl">Open scholarships</h2>
            <Link to="/education/scholarships" className="text-sm wofy-link inline-flex items-center gap-0.5">
              All scholarships <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {home.open_scholarships.length === 0 ? (
            <div className="wofy-card !p-6 text-center opacity-70">
              No scholarships posted yet. <Link to="/education/scholarships/new" className="wofy-link">Post one →</Link>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {home.open_scholarships.map(s => <ScholarshipCard key={s.id} s={s} />)}
            </div>
          )}
        </section>

        <section className="mb-4">
          <h2 className="font-display text-2xl mb-3">What you can do here</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Building2, title: "Register your institution", body: "Free profile for preschools, schools, colleges, universities, vocational schools and libraries." },
              { icon: Award,     title: "Post scholarships",         body: "Reach students who match your criteria — merit, need, sports, arts, minorities, international." },
              { icon: BookOpen,  title: "Publish admission info",    body: "Requirements, deadlines, entrance exams, fee structures — all on one clean page." },
              { icon: Users,     title: "Reconnect alumni (coming)", body: "Reunions, mentoring, career referrals, fundraising — all inside your institution's page." },
              { icon: Video,     title: "Show, don't just tell",     body: "Embed campus tour videos, faculty interviews and student stories from YouTube or Vimeo." },
              { icon: Sparkles,  title: "AI Education assistant (coming)", body: "College matcher, study plans, scholarship suggestions, resume drafts, exam prep." },
            ].map(({ icon: Icon, title, body }, i) => (
              <div key={i} className="wofy-card !p-4">
                <div className="w-9 h-9 rounded-lg bg-black/5 grid place-items-center mb-2"><Icon className="w-5 h-5" /></div>
                <div className="font-medium">{title}</div>
                <p className="text-sm opacity-80 mt-1">{body}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

function InstitutionCard({ i, typeLabel }) {
  return (
    <Link to={`/education/institutions/${i.slug}`}
      className="wofy-card !p-4 hover:shadow-md transition block"
      data-testid={`institution-card-${i.slug}`}>
      <div className="flex items-center gap-3 mb-2">
        {i.logo_url ? (
          <img src={i.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-black/5 grid place-items-center text-xs font-mono">
            {(i.name || "?")[0].toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <div className="font-medium truncate">{i.name}</div>
          <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">
            <GraduationCap className="w-3 h-3 inline mr-1" />{typeLabel(i.institution_type)}
          </div>
        </div>
      </div>
      {i.description && <p className="text-sm opacity-80 line-clamp-2">{i.description}</p>}
      {(i.city || i.country) && (
        <div className="text-xs opacity-70 flex items-center gap-1 mt-2">
          <MapPin className="w-3 h-3" />{[i.city, i.state, i.country].filter(Boolean).join(", ")}
        </div>
      )}
    </Link>
  );
}

function ScholarshipCard({ s }) {
  return (
    <Link to={`/education/scholarships/${s.slug}`}
      className="wofy-card !p-4 hover:shadow-md transition block"
      data-testid={`scholarship-card-${s.slug}`}>
      {s.cover_image_url && (
        <div className="w-full h-28 rounded-lg overflow-hidden mb-3 bg-black/5"
             style={{ backgroundImage: `url(${s.cover_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      )}
      <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">
        <Award className="w-3 h-3 inline mr-1" />{s.category}
      </div>
      <div className="font-medium mb-1 line-clamp-2">{s.name}</div>
      <div className="text-xs opacity-70">{s.provider}</div>
      {s.application_deadline && (
        <div className="text-xs opacity-70 mt-1 inline-flex items-center gap-1">
          <Calendar className="w-3 h-3" />Deadline: {s.application_deadline}
        </div>
      )}
      {s.amount_summary && <div className="text-xs opacity-70 mt-1">Amount: {s.amount_summary}</div>}
    </Link>
  );
}
